import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/lib/useSiteContent";

const PRO_LINKS = [
  { to: "/professionnels/optimisation-des-contrats", label: "Optimisation des contrats" },
  { to: "/professionnels/anticipation-des-contrats", label: "Anticipation des contrats" },
  { to: "/professionnels/gestion-multi-sites", label: "Gestion multi-sites" },
];
const PART_LINKS = [
  { to: "/particuliers/comparer-les-offres", label: "Comparer les offres" },
  { to: "/particuliers/changer-de-fournisseur", label: "Changer de fournisseur" },
  { to: "/particuliers/demenagement", label: "Déménagement" },
  { to: "/particuliers/comprendre-sa-facture", label: "Comprendre sa facture" },
];
const SIMPLE_LINKS = [
  { to: "/primes-cee", label: "Primes CEE" },
  { to: "/fournisseurs-partenaires", label: "Fournisseurs partenaires" },
  { to: "/veille-marche", label: "Veille Marché" },
  { to: "/a-propos", label: "À propos" },
];

/** Univers d'une page d'après son URL, ou null si la page est neutre. */
function universDe(pathname) {
  if (pathname.startsWith("/particuliers") || pathname.startsWith("/primes-cee")) return "particulier";
  if (pathname.startsWith("/professionnels")
      || pathname.startsWith("/fournisseurs-partenaires")
      || pathname.startsWith("/veille-marche")) return "pro";
  return null;
}

function Dropdown({ label, base, links, showOverview = true }) {
  // Actif seulement sur sa vue d'ensemble ou sur une de SES pages filles.
  // On ne peut pas se contenter du préfixe d'URL : Veille Marché et
  // Fournisseurs partenaires vivent aussi sous /particuliers/ sans appartenir
  // à cette rubrique, et allumeraient « Vue d'ensemble » à tort.
  const { pathname } = useLocation();
  const active =
    pathname === base ||
    links.some((l) => pathname === l.to || pathname.startsWith(`${l.to}/`));
  return (
    <div className="relative group">
      <NavLink
        to={base}
        aria-current={active ? "page" : undefined}
        className={`flex items-center gap-1 text-sm font-medium transition-colors py-2 px-3 rounded-full hover:bg-secondary/80 hover:text-primary ${
          active ? "bg-secondary font-semibold text-primary" : "text-muted-foreground"
        }`}
      >
        {label}
        <ChevronDown className="h-3.5 w-3.5" />
      </NavLink>
      <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
        <div className="bg-background border border-border rounded-2xl shadow-xl p-2 w-64">
          {showOverview && (
            <Link to={base} className="block px-4 py-2.5 rounded-xl text-sm font-semibold text-primary hover:bg-secondary">
              Vue d'ensemble
            </Link>
          )}
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block px-4 py-2.5 rounded-xl text-sm text-muted-foreground hover:bg-secondary hover:text-primary">
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { pathname } = useLocation();

  // Univers courant : l'URL fait foi, sinon le choix fait sur l'écran d'accueil
  // (posé par AudienceGate sur <html data-audience>). Null = on montre les deux.
  // Certaines pages n'ont pas l'univers dans leur URL mais appartiennent bien
  // à l'un des deux : /primes-cee est la version particuliers, tandis que
  // /fournisseurs-partenaires et /veille-marche sont les versions pro (leurs
  // jumelles particuliers vivent sous /particuliers/). Sans ça, une arrivée
  // directe sur ces pages sert le menu de l'autre univers.
  const audience = universDe(pathname)
    || (typeof document !== "undefined" ? document.documentElement.getAttribute("data-audience") : null);
  // Le menu ne sert jamais à changer d'univers : c'est le rôle de la pilule
  // Particuliers / Professionnels. On affiche donc toujours un seul univers,
  // « particulier » par défaut — comme la pilule, qui s'allume de ce côté
  // quand rien n'est connu.
  const proOnly = audience === "pro";
  const partOnly = !proOnly;

  // L'univers déduit de l'URL est mémorisé sur <html data-audience> : sans ça,
  // une page partagée comme /primes-cee ne sait plus d'où vient le visiteur et
  // le menu repasse en version neutre (les deux univers), ce qui donne
  // l'impression de basculer côté pro.
  useEffect(() => {
    const u = universDe(pathname);
    if (!u) return;
    document.documentElement.setAttribute("data-audience", u);
    try { sessionStorage.setItem("em_audience", u); } catch (e) { /* mode privé */ }
  }, [pathname]);

  // La pilule Particuliers / Professionnels ne doit jamais paraître vide :
  // on surligne l'univers de la page, sinon le choix fait sur l'écran
  // d'accueil, et à défaut « Particuliers ».
  const toggleSide = audience === "pro" ? "pro" : "particulier";
  const TOGGLE = [
    ["Particuliers", "/particuliers", "particulier"],
    ["Professionnels", "/professionnels", "pro"],
  ];

  // L'entrée CEE change de page et de nom selon l'univers : « Prime d'énergie »
  // (grand public) côté particulier, page dédiée aux entreprises côté pro.
  const PART_ALIASES = {
    "/fournisseurs-partenaires": "/particuliers/fournisseurs-partenaires",
    "/veille-marche": "/particuliers/veille-marche",
  };
  const simpleLinks = SIMPLE_LINKS.map((l) => {
    if (l.to === "/primes-cee") {
      if (partOnly) return { ...l, label: "Prime d'énergie" };
      if (audience === "pro") return { ...l, to: "/professionnels/primes-cee" };
      return l;
    }
    // Univers particulier : ces pages ont leur propre version.
    if (partOnly && PART_ALIASES[l.to]) return { ...l, to: PART_ALIASES[l.to] };
    return l;
  });
  const [open, setOpen] = useState(false);
  const [sub, setSub] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(false);

  const compact = scrolled && !hovered;

  const c = useSiteContent(["header.v2.logo"]);
  const logo = c("header.v2.logo", "/img/logo-energy-market.png");

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > lastY && y > 100);
        lastY = y;
        ticking = false;
      });
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSub(null);
  }, [pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-4 z-50 pointer-events-none left-4 right-4 lg:right-auto transition-all duration-500 ease-in-out ${
          scrolled ? "lg:left-4 lg:translate-x-0" : "lg:left-1/2 lg:-translate-x-1/2"
        }`}
      >
        <div className="flex items-center justify-between gap-3 lg:justify-start">
          <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            className={`flex items-center flex-shrink-0 rounded-full backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-md pointer-events-auto transition-all duration-500 ease-in-out ${
              compact
                ? "p-1 md:p-1.5 bg-background shadow-2xl border border-border"
                : "p-3 md:py-3 md:px-4 bg-background/70 border border-white/30"
            }`}
          >
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground hover:bg-secondary"
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {/* Le logo ramène à l'écran de choix Particulier / Professionnel.
               Navigation plein page (et non <Link>) : le bootstrap de
               l'AudienceGate ne s'exécute qu'au chargement du document. */}
            <a href="/?audience=reset" data-testid="logo-link" aria-label="Energy Market — écran d'accueil" className="px-1 inline-flex items-center shrink-0">
              <img src={logo} alt="Energy Market" className={`w-auto ${compact ? "h-14 md:h-16" : "h-11 md:h-12"}`} />
            </a>

            <div
              className={`hidden lg:flex items-center flex-nowrap transition-all duration-500 ease-in-out ${
                compact
                  ? "max-w-0 opacity-0 gap-0 overflow-hidden"
                  : "opacity-100 gap-4 md:gap-6 ml-2 overflow-visible"
              }`}
            >
              <nav className="hidden lg:flex items-center gap-1 flex-nowrap whitespace-nowrap" aria-label="Navigation principale">
                {proOnly ? (
                  <Dropdown label="Vue d'ensemble" base="/professionnels" links={PRO_LINKS} showOverview={false} />
                ) : (
                  <Dropdown label="Vue d'ensemble" base="/particuliers" links={PART_LINKS} showOverview={false} />
                )}
                {simpleLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) =>
                      `inline-flex items-center rounded-full px-4 py-2 text-sm transition-all hover:bg-secondary/80 hover:text-primary ${
                        isActive ? "bg-secondary font-semibold text-primary" : "font-medium text-muted-foreground"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </nav>
            </div>

          </div>

          {/* Toggle Particuliers / Professionnels — pilule séparée (mobile, à droite) */}
          <div className="lg:hidden flex items-center rounded-full bg-background/70 backdrop-blur-xl border border-white/30 shadow-md p-1 pointer-events-auto">
            {TOGGLE.map(([label, to, side]) => (
              <Link
                key={to}
                to={to}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  side === toggleSide ? "bg-accent text-accent-foreground" : "text-muted-foreground"
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          {!compact && (
            <div className="hidden lg:flex flex-col items-stretch gap-2 flex-shrink-0 pointer-events-auto">
              {/* Toggle Particuliers / Professionnels, au-dessus du CTA */}
              <div className="inline-flex rounded-full bg-background/80 backdrop-blur-xl border border-border shadow-md p-1">
                {TOGGLE.map(([label, to, side]) => (
                  <Link
                    key={to}
                    to={to}
                    data-testid={`header-toggle-${label.toLowerCase()}`}
                    className={`flex-1 text-center rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      side === toggleSide
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground hover:bg-secondary hover:text-primary"
                    }`}
                  >
                    {label}
                  </Link>
                ))}
              </div>
              <Button
                asChild
                data-testid="header-cta"
                className="inline-flex whitespace-nowrap rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-md"
              >
                <Link to="/contact?type=etude">Demander une étude gratuite</Link>
              </Button>
            </div>
          )}
        </div>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/95 backdrop-blur-md" data-testid="mobile-menu">
          <div className="flex justify-end p-6">
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer le menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground ring-1 ring-border"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav aria-label="Navigation mobile" className="mx-auto max-w-2xl px-6 mt-4 pb-12">
            <div className="flex flex-col gap-1">
              {(proOnly
                ? [{ key: "pro", label: "Vue d'ensemble", base: "/professionnels", links: PRO_LINKS, overview: false }]
                : [{ key: "part", label: "Vue d'ensemble", base: "/particuliers", links: PART_LINKS, overview: false }]
              ).map((grp) => (
                <div key={grp.key}>
                  <button
                    type="button"
                    onClick={() => setSub(sub === grp.key ? null : grp.key)}
                    className="w-full flex items-center justify-between py-2.5 font-medium"
                    aria-expanded={sub === grp.key}
                  >
                    {grp.label}
                    <ChevronDown className={`h-4 w-4 transition-transform ${sub === grp.key ? "rotate-180" : ""}`} />
                  </button>
                  {sub === grp.key && (
                    <div className="pl-4 flex flex-col">
                      {grp.overview && (
                        <Link to={grp.base} onClick={() => setOpen(false)} className="py-2 text-sm font-semibold text-primary">
                          Vue d'ensemble
                        </Link>
                      )}
                      {grp.links.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2 text-sm text-muted-foreground">
                          {l.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {simpleLinks.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="py-2.5 font-medium">
                  {l.label}
                </Link>
              ))}

              <Button asChild className="rounded-full mt-3 bg-accent text-accent-foreground font-bold">
                <Link to="/contact?type=etude" onClick={() => setOpen(false)}>
                  Demander une étude gratuite
                </Link>
              </Button>
            </div>
          </nav>
        </div>
      )}

      <div
        className={`fixed bottom-6 right-24 z-50 flex h-14 items-center transition-all duration-500 ease-out ${
          compact
            ? "translate-y-0 opacity-100 scale-100"
            : "translate-y-8 opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-lg pointer-events-auto">
          <Link to="/contact?type=etude">Demander une étude gratuite</Link>
        </Button>
      </div>
    </>
  );
}
